import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import ReactGA from 'react-ga4';

import Button from './Button';
import { Dropdown, Icon } from 'semantic-ui-react';
import { useEffect, useState } from 'react';
import * as dealSpreadsheetApi from '~/api/dealSpreadsheet';
import { usePortfolioContext, useProjectContext } from '~/hooks';
import axios from 'axios';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { logUserActivity, UserActivityTypes } from '~/api/user';
import styled from 'styled-components';
import { downloadPortfolioExcel, downloadProjectExcel, generateDealSpreadsheet, generateProjectSpreadsheet, state2abbreviation } from '~/utils';
import { getProjectPvSizeMWdc } from '~/utils/calculators/system';
import Modal from '~/components/Modal';
import { useModalContext } from '~/hooks/providers/ModalProvider';

const DropdownWrapper = styled.div<{ isPortfolio?: boolean }>`
  --color-primary: ${props => props.isPortfolio ? 'var(--color-blue)' : 'var(--color-orange)'};
`;

function DeleteDealSpreadsheetConfirmationModal() {
    const { closeModal, modalProps } = useModalContext();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleConfirmDelete = async () => {
        try {
            setIsDeleting(true);
            await modalProps.onConfirm();
            closeModal();
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Modal allowClose={!isDeleting} closeModal={isDeleting ? undefined : closeModal}>
            <Modal.Title>
                Delete uploaded deal spreadsheet?
            </Modal.Title>
            <Modal.Content>
                <p>
                    This will remove the manually uploaded deal spreadsheet.
                </p>
                <p>
                    You can still download a generated version after this action.
                </p>
            </Modal.Content>
            <Modal.Footer>
                <Button
                    primary
                    outlined
                    onClick={() => closeModal()}
                    disabled={isDeleting}
                >
                    Cancel
                </Button>
                <Button
                    primary
                    onClick={handleConfirmDelete}
                    loading={isDeleting}
                    disabled={isDeleting}
                >
                    Delete
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export function useDealSpreadsheet() {
    const { portfolio, displayBlobs, locations, portfolioTotals } = usePortfolioContext();
    const { project, formattedProject, projectCosts, projectDisplayValues } = useProjectContext();
    const { permissions } = usePermissions();
    const { openModal } = useModalContext();
    const [hasUploadedDoc, setHasUploadedDoc] = useState(false);

    //  Summary docs are always stored in the parent and accessible to all investors
    const portfolioId = portfolio?.parentId ?? portfolio?.id;
    const projectId = project?.parentId ?? project?.id;
    const dealId = portfolio ? portfolioId : projectId;
    const dealType = portfolio ? 'PORTFOLIO' : 'PROJECT';

    const { mutateAsync: fetchExistingDocument } = useMutation(async () => {
        try {
            return await dealSpreadsheetApi.getDealSpreadsheetDownloadUrl(dealId, dealType);
        } catch (err) {
            // Need this for when there is no document found
        }
    });

    useEffect(() => {
        fetchExistingDocument()
            .then(data => setHasUploadedDoc(!!data?.presignedUrl));
    }, [fetchExistingDocument]);

    const { mutateAsync: downloadFile, status: downloadStatus } = useMutation(async () => {
        try {
            ReactGA.event({
                category: 'Downloads',
                action: 'Download deal spreadsheet',
                label: (project ? project?.name : portfolio?.name) || 'Unknown project'
            });
            logUserActivity({
                activityType: UserActivityTypes.DEAL_SPREADSHEET_DOWNLOAD,
                ...(portfolio ? { portfolioId } : { projectId }),
            });
            // Navigate to the presigned S3 url to trigger the download
            fetchExistingDocument()
                .then(data => window.location = data?.presignedUrl);
        } catch (err) {
            console.error(err);
            toast.error('Error occurred downloading the document. Please refresh the page and try again.');
        }
    });

    const { mutateAsync: removeFile, status: removeFileStatus } = useMutation(async () => {
        try {
            await dealSpreadsheetApi.removeDealSpreadsheet(dealId, dealType);
            setHasUploadedDoc(false);
            toast.success('Document removed');
        } catch (err) {
            console.error(err);
            toast.error('Error occurred removing the document. Please refresh the page and try again.');
        }
    });

    const handleDeleteUploadedSpreadsheet = () => {
        openModal(DeleteDealSpreadsheetConfirmationModal, {
            onConfirm: removeFile
        });
    };

    const generateFileName = (file: File) => {

        // Get project or portfolio name
        let name = portfolio ? portfolio.name : project.name;
        if (project && project.portfolio) {
            name = `${project.portfolio.name} - ${project.name}`;
        }
        // Get 2-letter state abbreviation
        let stateAbbrev = ''; // default fallback
        if (portfolio) {
            stateAbbrev = 'Entire portfolio -';
        } else if (project) {
            stateAbbrev = state2abbreviation(project.addressState) + ' -' || 'No address -';
        }

        // Get PV size in MW with 3 decimals
        let pvSizeMW = '0.000';
        if (portfolio && portfolioTotals) {
            pvSizeMW = ((portfolioTotals.sizeKwdc || 0) / 1000).toFixed(3);
        } else if (project && project?.projectArrays && project?.projectArrays?.length > 0) {
            pvSizeMW = ((getProjectPvSizeMWdc(project) || 0)).toFixed(3);
        }

        // Generate date stamp (YYYY-MM-DD format)
        const dateStamp = new Date().toISOString().split('T')[0];

        // Get file extension
        const fileExtension = file.name.split('.').pop();

        return `${name} - ${stateAbbrev} ${pvSizeMW}MW - ${dateStamp}.${fileExtension}`;
    };

    const { mutateAsync: uploadFile, status: uploadStatus } = useMutation(async (file: File) => {
        try {
            const result = await dealSpreadsheetApi.uploadDealSpreadsheet(dealId, dealType, generateFileName(file));

            if (result?.presignedUrl) {
                await axios.put(result.presignedUrl, file);
                setHasUploadedDoc(true);
                toast.success('Document uploaded');
            } else {
                throw new Error('No presigned url returned when uploading deal spreadsheet');
            }
        } catch (err) {
            console.error(err);
            toast.error('Error occurred uploading the deal spreadsheet. Please refresh the page and try again.');
        }
    });

    const { mutateAsync: replaceFile, status: replaceFileStatus } = useMutation(async (file: File) => {
        try {
            const result = await dealSpreadsheetApi.updateDealSpreadsheet(dealId, dealType, generateFileName(file));

            if (result?.presignedUrl) {
                await axios.put(result.presignedUrl, file);
                toast.success('Replacement spreadsheet uploaded');
            } else {
                throw new Error('No presigned url returned when uploading spreadsheet');
            }
        } catch (err) {
            console.error(err);
            toast.error('Error occurred replacing the spreadsheet. Please refresh the page and try again.');
        }
    });

    const dropzone = useDropzone({
        maxFiles: 1,
        async onDrop(acceptedFiles) {
            if (acceptedFiles?.length !== 1) {
                toast.error('Only one file can be uploaded for a row.');
            } else {
                hasUploadedDoc ?
                    await replaceFile(acceptedFiles[0])
                    : await uploadFile(acceptedFiles[0]);
            }
        }
    });

    const handleGenerateSpreadsheet = () => {
        logUserActivity({
            activityType: UserActivityTypes.DEAL_SPREADSHEET_DOWNLOAD,
            ...(portfolio ? { portfolioId } : { projectId }),
        });
        if (portfolio) {
            generateDealSpreadsheet(portfolio, portfolioTotals, displayBlobs);
        } else {
            generateProjectSpreadsheet(formattedProject, projectCosts, projectDisplayValues);
        }

    };

    const handleGenerateLegacySpreadsheet = () => {
        logUserActivity({
            activityType: UserActivityTypes.DEAL_SPREADSHEET_DOWNLOAD,
            ...(portfolio ? { portfolioId } : { projectId }),
        });
        if (portfolio) {
            downloadPortfolioExcel(portfolio, locations, portfolioTotals, displayBlobs, permissions);
        } else {
            downloadProjectExcel(formattedProject, projectCosts, projectDisplayValues, permissions);
        }
    };

    return {
        isPortfolio: !!portfolio,
        hasUploadedDoc,
        downloadStatus,
        uploadStatus,
        removeFileStatus,
        replaceFileStatus,
        downloadFile,
        handleDeleteUploadedSpreadsheet,
        handleGenerateSpreadsheet,
        handleGenerateLegacySpreadsheet,
        openFilePicker: dropzone.open,
        getRootProps: dropzone.getRootProps,
        getInputProps: dropzone.getInputProps,
    };
}

export default function DealSpreadsheetButton() {
    const { permissions } = usePermissions();
    const {
        isPortfolio,
        hasUploadedDoc,
        downloadStatus,
        uploadStatus,
        removeFileStatus,
        replaceFileStatus,
        downloadFile,
        handleDeleteUploadedSpreadsheet,
        handleGenerateSpreadsheet,
        handleGenerateLegacySpreadsheet,
        openFilePicker,
        getRootProps,
    } = useDealSpreadsheet();

    if (permissions.isAdmin) {
        return (
            <div>
                <DropdownWrapper isPortfolio={isPortfolio}>
                    <Dropdown
                        icon={null}
                        direction='left'
                        trigger={
                            <Button
                                secondary
                                outlined
                                size='small'
                                loading={[uploadStatus, removeFileStatus, replaceFileStatus].includes('loading')}
                            >
                                <Icon name='file alternate outline' />
                                Deal spreadsheet
                            </Button>
                        }
                    >
                        <Dropdown.Menu>
                            {hasUploadedDoc ? (
                                <>
                                    <Dropdown.Item onClick={() => downloadFile()}>
                                        Download
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => openFilePicker()}>
                                        Replace
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDeleteUploadedSpreadsheet()}>
                                        Delete
                                    </Dropdown.Item>
                                </>
                            ) :
                                <Dropdown.Item
                                    {...(uploadStatus === 'loading' ? {} : getRootProps())}
                                >
                                    <Icon name='cloud upload' />
                                    Upload new version
                                </Dropdown.Item>
                            }
                            <Dropdown.Item onClick={handleGenerateSpreadsheet}>
                                Download generated version
                            </Dropdown.Item>
                            <Dropdown.Item onClick={handleGenerateLegacySpreadsheet}>
                                Download legacy generated version
                            </Dropdown.Item>

                        </Dropdown.Menu>
                    </Dropdown>
                </DropdownWrapper>

            </div>
        );
    } else if (permissions.isInvestor && hasUploadedDoc) {
        return (
            <div>
                <Button
                    secondary
                    outlined
                    loading={downloadStatus === 'loading'}
                    {...(downloadStatus === 'loading' ? {} : getRootProps())}
                    size='small'
                    onClick={() => downloadFile()}
                >
                    <Icon name='file alternate outline' />
                    Deal spreadsheet
                </Button>
            </div>
        );
    } else {
        return (
            <Button className="outlined secondary" size="small" onClick={handleGenerateLegacySpreadsheet}>
                <Icon name='file alternate outline' />
                Deal spreadsheet
            </Button>
        );
    }
}
